import { Controller } from "@hotwired/stimulus"

// Store the categories the user has *explicitly disabled*. Anything not in this
// list — including categories added in later releases — defaults to enabled, so
// new pills don't silently filter out new result types for returning users.
const DISABLED_STORAGE_KEY = "account_search_filter_disabled_categories"
const ALL_CATEGORIES = ["agency", "property", "job", "contact", "asset"]

export default class extends Controller {
  static targets = ["input", "hint", "count", "list", "empty", "filterPill"]

  static values = {
    url: String,
    accountLabel: String,
    agencyLabel: String,
    propertyLabel: String,
    tenancyLabel: String,
    jobLabel: String,
    assetLabel: String
  }

  connect() {
    this.debounceTimer = null
    this.lastPayload = null
    this.enabledCategories = this.loadEnabledCategories()
    this.syncFilterPills()
    this.clearResults()
  }

  loadEnabledCategories() {
    let disabled = []
    try {
      const raw = window.localStorage.getItem(DISABLED_STORAGE_KEY)
      if (raw) {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) disabled = parsed
      }
    } catch (_) {
      // localStorage may be unavailable — fall through with no disables
    }
    return new Set(ALL_CATEGORIES.filter((c) => !disabled.includes(c)))
  }

  persistEnabledCategories() {
    const disabled = ALL_CATEGORIES.filter((c) => !this.enabledCategories.has(c))
    try {
      window.localStorage.setItem(DISABLED_STORAGE_KEY, JSON.stringify(disabled))
    } catch (_) {
      // ignore
    }
  }

  syncFilterPills() {
    this.filterPillTargets.forEach((pill) => {
      const category = pill.dataset.category
      const active = this.enabledCategories.has(category)
      pill.classList.toggle("active", active)
      pill.setAttribute("aria-pressed", active ? "true" : "false")
    })
  }

  toggleFilter(event) {
    const pill = event.currentTarget
    const category = pill.dataset.category
    if (!category) return

    if (this.enabledCategories.has(category)) {
      this.enabledCategories.delete(category)
    } else {
      this.enabledCategories.add(category)
    }

    this.syncFilterPills()
    this.persistEnabledCategories()

    if (this.lastPayload) {
      this.renderResults(this.lastPayload)
    }
  }

  search() {
    const query = this.inputTarget.value.trim()

    if (query.length < 3) {
      this.showHint()
      this.clearResults()
      return
    }

    this.hideHint()
    window.clearTimeout(this.debounceTimer)
    this.debounceTimer = window.setTimeout(() => {
      this.fetchResults(query)
    }, 250)
  }

  fetchResults(query) {
    fetch(`${this.urlValue}?query=${encodeURIComponent(query)}`, {
      headers: { Accept: "application/json" }
    })
      .then((response) => response.json())
      .then((payload) => {
        this.lastPayload = payload
        this.renderResults(payload)
      })
      .catch(() => {
        this.lastPayload = null
        this.clearResults()
        this.showEmpty()
      })
  }

  renderResults(payload) {
    const allResults = (payload && payload.results) || []
    const results = allResults.filter((item) =>
      this.enabledCategories.has(item.category)
    )
    const totalCount = results.length

    const counts = { agency: 0, property: 0, job: 0, contact: 0, asset: 0 }
    results.forEach((item) => {
      if (counts[item.category] !== undefined) counts[item.category] += 1
    })

    this.listTarget.innerHTML = ""
    this.hideEmpty()

    if (totalCount > 0) {
      this.countTarget.classList.remove("d-none")
      const parts = []
      this.filterPillTargets.forEach((pill) => {
        const cat = pill.dataset.category
        if (!this.enabledCategories.has(cat)) return
        const label = pill.textContent.trim()
        parts.push(`${label}: ${counts[cat] || 0}`)
      })
      this.countTarget.textContent = `Found ${totalCount} result${totalCount === 1 ? "" : "s"} ( ${parts.join(", ")} )`
    } else {
      this.countTarget.classList.add("d-none")
      this.showEmpty()
    }

    results.forEach((item) => {
      const link = document.createElement("a")
      link.href = item.url
      link.className = "list-group-item list-group-item-action"

      const header = document.createElement("div")
      header.className = "d-flex align-items-center justify-content-between"

      const title = document.createElement("div")
      title.className = "fw-semibold"
      if (item.type === "property") {
        title.innerHTML = `<span class=\"fw-semibold\">${this.propertyLabelValue}:</span> ${item.name}`
      } else if (item.type === "agency") {
        title.innerHTML = `<span class=\"fw-semibold\">${this.agencyLabelValue}:</span> ${item.name}`
      } else {
        title.textContent = item.name
      }

      const badge = document.createElement("span")
      badge.className = "badge text-bg-secondary"
      badge.textContent = item.type_label || this.typeLabel(item.type)

      header.appendChild(title)
      header.appendChild(badge)

      const address = document.createElement("div")
      address.className = "text-body-tertiary fs-9"
      address.textContent = item.address || ""

      link.appendChild(header)
      link.appendChild(address)

      if (item.type === "property" || item.type === "tenancy") {
        const agency = document.createElement("div")
        agency.className = "text-body-tertiary fs-9"
        if (item.agency_name) {
          agency.innerHTML = `<span class=\"fw-semibold\">Agency:</span> ${item.agency_name}`
        } else {
          agency.innerHTML = `<span class=\"fw-semibold\">Agency:</span> -`
        }
        link.appendChild(agency)
      }

      if (item.meta) {
        const meta = document.createElement("div")
        meta.className = "text-body-tertiary fs-9"
        if (item.type === "account") {
          meta.innerHTML = `<span class=\"fw-semibold\">Account:</span> ${item.meta}`
        } else if (item.type === "property" || item.type === "tenancy") {
          meta.innerHTML = `<span class=\"fw-semibold\">${this.propertyLabelValue}:</span> ${item.meta}`
        } else {
          meta.textContent = item.meta
        }
        link.appendChild(meta)
      }

      this.listTarget.appendChild(link)
    })
  }

  clearResults() {
    this.lastPayload = null
    this.listTarget.innerHTML = ""
    this.countTarget.classList.add("d-none")
    this.hideEmpty()
  }

  showHint() {
    this.hintTarget.classList.remove("d-none")
  }

  hideHint() {
    this.hintTarget.classList.add("d-none")
  }

  showEmpty() {
    this.emptyTarget.classList.remove("d-none")
  }

  hideEmpty() {
    this.emptyTarget.classList.add("d-none")
  }

  typeLabel(type) {
    if (type === "account") return this.accountLabelValue
    if (type === "agency") return this.agencyLabelValue
    if (type === "property") return this.propertyLabelValue
    if (type === "tenancy") return this.tenancyLabelValue
    if (type === "job") return this.jobLabelValue
    if (type === "asset") return this.assetLabelValue || "Asset"
    return type
  }
}
